import { useEffect, useState } from "react";
import { subscribeSubmitError } from "../lib/submit-error.js";

/**
 * Global non-blocking toast for a permanently-failed answer submission
 * (reliability fast-follow). Mounted once at the app root; subscribes to
 * reportSubmitError. Queueable failures (offline / 5xx / 429) are already
 * handled optimistically by submitAttempt's offline outbox, so this only fires
 * on a PERMANENT error (e.g. a 4xx) that the six bespoke drills previously
 * swallowed with `.catch(() => undefined)`. Auto-dismisses; a11y-announced.
 */
export function SyncErrorToast() {
  const [visible, setVisible] = useState(false);

  useEffect(() => subscribeSubmitError(() => setVisible(true)), []);

  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setVisible(false), 6000);
    return () => clearTimeout(t);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: "fixed",
        left: "50%",
        bottom: "calc(88px + env(safe-area-inset-bottom))",
        transform: "translateX(-50%)",
        zIndex: 60,
        maxWidth: "min(92vw, 420px)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 8,
        background: "var(--bg-1)",
        border: "1px solid var(--signal-red)",
        boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
        fontSize: 13,
        color: "var(--fg-0)",
        lineHeight: 1.4,
      }}
    >
      <span style={{ flex: 1 }}>
        That answer didn't save. Check your connection; your other answers are safe.
      </span>
      <button
        type="button"
        onClick={() => setVisible(false)}
        aria-label="Dismiss"
        style={{
          background: "none",
          border: "none",
          color: "var(--fg-3)",
          cursor: "pointer",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          padding: "4px 6px",
          minHeight: 44,
          minWidth: 44,
        }}
      >
        ✕
      </button>
    </div>
  );
}
